// RecorderView — entrada do Gravador de Aulas.
//
// 2 modos:
//   - lista: cards de gravações + botão "Nova gravação"
//   - detalhe: VIEW inline (não modal) com header + 2 colunas lado a lado
//     em telas largas (Resumo + Transcrição). Markdown renderizado, e o
//     transcript tem badges interativos pros falantes (clicar renomeia em
//     TODO o lugar onde a tag aparece — transcript + summary).

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  FileAudio,
  FileText,
  Loader2,
  Mic,
  Pencil,
  Plus,
  Share2,
  Sparkles,
  Trash2,
  Upload,
  Volume2
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { setPersistentPlayerSuppressed } from "@app/ui";
import { api, ApiError, type LectureRow } from "./api";
import { subCrumbs } from "./App";
import { openConfirm, openAlert } from "./ConfirmPromptModal";
import { openShareDialog } from "./ShareDialog";
import { flagEnabled } from "@app/ui/lib/feature-flags";
import { AiMarkdownBlock } from "./aiMarkdown";
import { ImportMediaModal } from "./ImportMediaModal";

const phase = signal<"loading" | "ready" | "error">("loading");
const lectures = signal<LectureRow[]>([]);
const errMsg = signal<string | null>(null);
const openId = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listLectures();
    lectures.value = r.lectures;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = e instanceof Error ? e.message : String(e);
    phase.value = "error";
  }
}

function openRecorderPopup() {
  const features = [
    "width=960",
    "height=720",
    "menubar=no",
    "toolbar=no",
    "location=no",
    "status=no",
    "resizable=yes",
    "scrollbars=yes"
  ].join(",");
  const w = window.open(
    "/student/?view=recorder-window",
    "lecture-recorder",
    features
  );
  if (!w) {
    void openAlert({
      title: "Pop-up bloqueado",
      message:
        "O navegador bloqueou a janela de gravação. Permita pop-ups pra esse site (clique no ícone do bloqueio na barra de endereço) e tente de novo."
    });
    return;
  }
  const checker = setInterval(() => {
    if (w.closed) {
      clearInterval(checker);
      void load();
    }
  }, 1000);
}

const KIND_LABEL: Record<string, string> = {
  aula: "Aula",
  palestra: "Palestra",
  reuniao: "Reunião",
  entrevista: "Entrevista",
  estudo: "Estudo",
  outro: "Outro"
};

const STATUS_META: Record<
  string,
  { label: string; tone: "ok" | "warn" | "info" | "muted" | "error" }
> = {
  draft: { label: "Rascunho", tone: "muted" },
  processing: { label: "Processando", tone: "info" },
  ready: { label: "Pronto", tone: "ok" },
  failed: { label: "Falhou", tone: "error" }
};

function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m${s ? ` ${s}s` : ""}`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

/** Abre file picker, lê áudio local que o usuário baixou anteriormente, e
 *  envia pra `lectureId` retomar a transcrição que tinha ficado em draft/
 *  failed. Usado pra recuperar quando o pop-up foi fechado antes do upload. */
async function resumeTranscribe(lec: LectureRow): Promise<void> {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "audio/webm,audio/*,video/webm,video/*";
  input.style.display = "none";
  document.body.appendChild(input);
  const fileChosen = new Promise<File | null>((resolve) => {
    input.addEventListener("change", () => {
      const f = input.files?.[0] ?? null;
      resolve(f);
    });
    input.addEventListener("cancel", () => resolve(null));
  });
  input.click();
  const file = await fileChosen;
  document.body.removeChild(input);
  if (!file) return;
  try {
    await api.transcribeLecture(
      lec.id,
      file,
      lec.durationSeconds || Math.round(file.size / 12000) // estimativa fallback
    );
    void openAlert({
      title: "Transcrição iniciada",
      message:
        "O áudio foi enviado. Em poucos minutos o conteúdo aparecerá nessa gravação — você receberá uma notificação quando estiver pronto."
    });
    await load();
  } catch (e) {
    void openAlert({
      title: "Falha ao retomar",
      message: e instanceof Error ? e.message : String(e)
    });
  }
}

export function RecorderView() {
  const [importOpen, setImportOpen] = useState(false);
  useEffect(() => {
    void load();
  }, []);

  // Mata o PersistentPlayer enquanto o Gravador está aberto — música de fundo
  // entra em qualquer captura via "tab/system audio" e o player também rouba
  // foco visual num contexto que pede silêncio. Restaurado ao desmontar.
  useEffect(() => {
    setPersistentPlayerSuppressed(true);
    return () => setPersistentPlayerSuppressed(false);
  }, []);

  // Quando uma gravação está selecionada, RENDERIZA O DETALHE — view in-place
  // (não modal). Aluno volta clicando no "Voltar" do header.
  if (openId.value) {
    return (
      <LectureDetailView
        id={openId.value}
        onBack={() => (openId.value = null)}
        onDeleted={() => {
          openId.value = null;
          void load();
        }}
      />
    );
  }

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="flex items-center gap-2 text-xl font-bold">
            <Mic className="size-5 text-violet-600 dark:text-violet-400" />
            Gravador de Aulas
          </h1>
          <p class="mt-1 max-w-2xl text-sm text-muted-foreground">
            Grave aula, palestra, reunião ou estudo. O áudio fica salvo só no
            seu dispositivo — a plataforma transcreve, resume com IA e guarda
            o texto pra você acessar depois.
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            class="inline-flex items-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/5 px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
          >
            <Upload className="size-4" />
            Importar áudio/vídeo
          </button>
          <button
            type="button"
            onClick={openRecorderPopup}
            class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
          >
            <Plus className="size-4" />
            Nova gravação
          </button>
        </div>
      </header>

      {importOpen && (
        <ImportMediaModal
          onClose={() => setImportOpen(false)}
          onSuccess={(lectureId) => {
            setImportOpen(false);
            openId.value = lectureId;
            void load();
          }}
        />
      )}

      {phase.value === "loading" && (
        <div class="space-y-2">
          <Skeleton class="h-24 w-full" />
          <Skeleton class="h-24 w-full" />
        </div>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <AlertCircle className="mr-2 inline size-4 text-destructive" />
          {errMsg.value ?? "Erro ao carregar."}
        </div>
      )}

      {phase.value === "ready" && lectures.value.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-10 text-center">
          <FileAudio className="mx-auto size-10 text-muted-foreground" />
          <h3 class="mt-3 text-base font-semibold">Nenhuma gravação ainda</h3>
          <p class="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Clique em "Nova gravação" pra começar. A janela do gravador abre
            em pop-up separado pra evitar que você perca a gravação por
            refresh acidental.
          </p>
        </div>
      )}

      {phase.value === "ready" && lectures.value.length > 0 && (
        <ul class="space-y-2">
          {lectures.value.map((l) => (
            <LectureRowItem
              key={l.id}
              lecture={l}
              onClick={() => (openId.value = l.id)}
              onDelete={async () => {
                const ok = await openConfirm({
                  title: "Excluir gravação?",
                  message: `"${l.title}" e a transcrição associada serão removidos. Essa ação não pode ser desfeita.`,
                  confirmLabel: "Excluir",
                  danger: true
                });
                if (!ok) return;
                await api.deleteLecture(l.id);
                await load();
              }}
              onResume={() => void resumeTranscribe(l)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: LectureRow["status"] }) {
  const meta = STATUS_META[status] ?? STATUS_META.draft;
  const tone = meta.tone;
  const cls =
    tone === "ok"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "warn"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : tone === "info"
          ? "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300"
          : tone === "error"
            ? "border-destructive/40 bg-destructive/10 text-destructive"
            : "border-border bg-muted text-muted-foreground";
  return (
    <span
      class={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {status === "processing" && <Loader2 className="mr-1 inline size-3 animate-spin" />}
      {meta.label}
    </span>
  );
}

function LectureRowItem({
  lecture,
  onClick,
  onDelete,
  onResume
}: {
  lecture: LectureRow;
  onClick: () => void;
  onDelete: () => void | Promise<void>;
  onResume: () => void;
}) {
  const canResume = lecture.status === "draft" || lecture.status === "failed";
  return (
    <li class="group flex items-stretch gap-1 rounded-md border border-border bg-card transition-colors hover:border-violet-500/40 hover:bg-violet-500/5">
      <button
        type="button"
        onClick={onClick}
        class="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left"
      >
        <div class="grid size-10 shrink-0 place-items-center rounded-md bg-violet-500/15 text-violet-600 dark:text-violet-400">
          <Mic className="size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-baseline gap-2">
            <span class="truncate text-sm font-semibold">{lecture.title}</span>
            <StatusBadge status={lecture.status} />
            {lecture.sourceLectureId && (
              <span class="inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
                <Share2 className="size-2.5" />
                {lecture.sharedByName ? `Por ${lecture.sharedByName.split(" ")[0]}` : "Compartilhada"}
              </span>
            )}
          </div>
          <div class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            <span class="rounded bg-muted px-1.5 py-0.5">
              {KIND_LABEL[lecture.kind] ?? lecture.kind}
            </span>
            {lecture.theme && <span class="truncate">{lecture.theme}</span>}
            {lecture.durationSeconds > 0 && (
              <span class="inline-flex items-center gap-0.5">
                <Clock className="size-3" />
                {fmtDuration(lecture.durationSeconds)}
              </span>
            )}
            <span class="inline-flex items-center gap-0.5">
              <Calendar className="size-3" />
              {fmtDate(lecture.createdAt)}
            </span>
          </div>
        </div>
      </button>
      {canResume && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onResume();
          }}
          title="Retomar transcrição: escolha o arquivo .webm que foi baixado quando a gravação terminou"
          aria-label={`Retomar transcrição da gravação ${lecture.title}`}
          class="m-2 inline-flex items-center gap-1 self-center rounded-md border border-violet-500/40 bg-violet-500/10 px-2.5 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
        >
          <Upload className="size-3.5" />
          Retomar
        </button>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onDelete();
        }}
        title={`Excluir "${lecture.title}"`}
        aria-label={`Excluir gravação ${lecture.title}`}
        class="m-2 self-center rounded-md p-2 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus:opacity-100"
      >
        <Trash2 className="size-4" />
      </button>
    </li>
  );
}

// =============================================================================
// Helpers de naming + SRT
// =============================================================================

/** Nome canônico do arquivo da gravação — usado no download local e no SRT
 *  pra player reconhecer como legenda automaticamente. Determinístico a partir
 *  do title + createdAt da lecture. */
function lectureBasename(lec: LectureRow): string {
  const safeTitle = lec.title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "gravacao";
  const ts = lec.createdAt.slice(0, 16).replace(/[:T]/g, "-");
  return `${safeTitle}-${ts}`;
}

/** Converte segundos (number) pra timestamp SRT (HH:MM:SS,mmm). */
function secsToSrtTime(sec: number): string {
  const total = Math.max(0, Math.floor(sec * 1000));
  const ms = total % 1000;
  const s = Math.floor(total / 1000) % 60;
  const m = Math.floor(total / 60000) % 60;
  const h = Math.floor(total / 3600000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")},${String(ms).padStart(3, "0")}`;
}

/** Parse "MM:SS" ou "HH:MM:SS" pra segundos. Null se inválido. */
function parseTimecodeToSecs(tc: string): number | null {
  const parts = tc.split(":").map((p) => parseInt(p, 10));
  if (parts.some((p) => !Number.isFinite(p))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

/** Gera SRT a partir do transcript + duração total. Cada turn vira um cue;
 *  a duração de um turn é estimada como (início do próximo) - (início do
 *  atual). Último turn dura até `totalDurationSec`. Se um turn não tem
 *  timestamp, distribuímos uniformemente pelo intervalo restante. */
function generateSrt(transcript: string, totalDurationSec: number): string {
  const turns = parseTranscript(transcript);
  if (turns.length === 0) return "";
  // Resolve start/end secs por turn
  const cues: Array<{ start: number; end: number; text: string }> = [];
  // Primeiro passe — atribui start a quem tem timestamp
  const starts: (number | null)[] = turns.map((t) =>
    t.time ? parseTimecodeToSecs(t.time) : null
  );
  // Fallback: turns sem time recebem interpolação linear
  for (let i = 0; i < starts.length; i++) {
    if (starts[i] != null) continue;
    // Acha o anterior conhecido e o próximo conhecido pra interpolar
    let prev = i - 1;
    while (prev >= 0 && starts[prev] == null) prev--;
    let next = i + 1;
    while (next < starts.length && starts[next] == null) next++;
    const prevSec = prev >= 0 ? (starts[prev] as number) : 0;
    const nextSec = next < starts.length ? (starts[next] as number) : totalDurationSec;
    const gap = nextSec - prevSec;
    const slot = gap / (next - prev);
    starts[i] = prevSec + slot * (i - prev);
  }
  // Constrói cues
  for (let i = 0; i < turns.length; i++) {
    const start = starts[i] as number;
    const end =
      i + 1 < turns.length
        ? (starts[i + 1] as number)
        : Math.max(start + 2, totalDurationSec);
    const speakerPrefix = turns[i].speaker ? `[${turns[i].speaker}] ` : "";
    const text = `${speakerPrefix}${turns[i].text}`.trim();
    if (text) {
      cues.push({ start, end: Math.max(start + 0.5, end), text });
    }
  }
  // Serializa
  return cues
    .map(
      (c, i) =>
        `${i + 1}\n${secsToSrtTime(c.start)} --> ${secsToSrtTime(c.end)}\n${c.text}\n`
    )
    .join("\n");
}

// =============================================================================
// LectureDetailView — view in-place (não modal), com 2 colunas lado a lado em
// telas largas. Rename de speaker propaga em transcript + summary via PATCH.
// =============================================================================

function LectureDetailView({
  id,
  onBack,
  onDeleted
}: {
  id: string;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const [lec, setLec] = useState<LectureRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.getLecture(id);
      setLec(r.lecture);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // Polling enquanto processa
    const t = setInterval(() => {
      if (lec?.status === "processing") void refresh();
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lec?.status]);

  // Empurra breadcrumb com o título da gravação enquanto o detail está aberto.
  useEffect(() => {
    if (!lec) return;
    subCrumbs.value = [
      {
        label: lec.title,
        onClick: onBack
      }
    ];
    return () => {
      subCrumbs.value = [];
    };
  }, [lec?.id, lec?.title, onBack]);

  async function saveTitle() {
    if (!lec) return;
    const next = titleDraft.trim();
    if (!next || next === lec.title) {
      setEditingTitle(false);
      return;
    }
    try {
      const r = await api.updateLecture(lec.id, { title: next });
      setLec(r.lecture);
      setEditingTitle(false);
    } catch (e) {
      void openAlert({
        title: "Erro ao renomear",
        message: e instanceof Error ? e.message : String(e)
      });
    }
  }

  function exportSrt() {
    if (!lec || !lec.transcript) return;
    const srt = generateSrt(lec.transcript, lec.durationSeconds || 60);
    if (!srt) {
      void openAlert({
        title: "Nada pra exportar",
        message:
          "Transcrição não tem turnos identificáveis. Verifique se a gravação foi processada."
      });
      return;
    }
    const blob = new Blob([srt], { type: "application/x-subrip" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${lectureBasename(lec)}.srt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function remove() {
    if (!lec) return;
    const ok = await openConfirm({
      title: "Excluir gravação?",
      message: `"${lec.title}" e a transcrição associada serão removidos. Essa ação não pode ser desfeita.`,
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    await api.deleteLecture(id);
    onDeleted();
  }

  /** Renomeia uma tag de falante (ex: "Voz A" → "Prof Maria") em TODOS os
   *  lugares onde aparece — tanto no transcript quanto no summary. Persiste
   *  via PATCH e atualiza estado local. */
  async function renameSpeaker(oldTag: string, newName: string) {
    if (!lec) return;
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldTag) return;
    // Sanitiza nome — remove [ ] pra evitar tag aninhada
    const safe = trimmed.replace(/[\[\]]/g, "").trim();
    if (!safe) return;
    const escaped = oldTag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // 1) Forma canônica: [Voz A] → [Nome]
    const reBracket = new RegExp(`\\[${escaped}\\]`, "g");
    let newTranscript = (lec.transcript ?? "").replace(reBracket, `[${safe}]`);
    let newSummary = (lec.summary ?? "").replace(reBracket, `[${safe}]`);
    // 2) Fallback word-boundary: "Voz A" sem colchetes (só pra padrão "Voz X"
    //    e similares; evita falso-positivo se oldTag for um nome qualquer).
    if (/^Voz\s+[A-Z](?:[a-z]+)?$/.test(oldTag)) {
      const reWord = new RegExp(`\\b${escaped}\\b`, "g");
      newTranscript = newTranscript.replace(reWord, safe);
      newSummary = newSummary.replace(reWord, safe);
    }
    try {
      await api.updateLectureText(id, newTranscript, newSummary);
      setLec({
        ...lec,
        transcript: newTranscript,
        summary: newSummary
      });
    } catch (e) {
      void openAlert({
        title: "Erro ao renomear",
        message: e instanceof Error ? e.message : String(e)
      });
    }
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* noop */
    }
  }

  async function downloadText(filename: string, text: string) {
    const blob = new Blob([text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div class="space-y-4">
      <header class="space-y-2">
        <div class="flex flex-wrap items-start gap-3">
          <div class="grid size-10 shrink-0 place-items-center rounded-md bg-violet-500/15 text-violet-600">
            <Mic className="size-5" />
          </div>
          <div class="min-w-0 flex-1">
            {editingTitle && lec ? (
              <div class="flex items-center gap-1">
                <input
                  type="text"
                  value={titleDraft}
                  onInput={(e) =>
                    setTitleDraft((e.target as HTMLInputElement).value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void saveTitle();
                    if (e.key === "Escape") {
                      // Reseta draft ANTES de sair do modo edit pra evitar que
                      // o onBlur dispare um saveTitle com o valor "cancelado".
                      setTitleDraft(lec.title);
                      setEditingTitle(false);
                    }
                  }}
                  onBlur={() => void saveTitle()}
                  autoFocus
                  aria-label="Título da gravação"
                  title="Título da gravação"
                  class="w-full rounded-md border border-primary bg-background px-2 py-1 text-lg font-bold outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  type="button"
                  // onMouseDown (não onClick): blur do input dispara antes do
                  // click. Capturando mousedown a gente evita race onde o blur
                  // salva e fecha o modo edit antes do click chegar.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    void saveTitle();
                  }}
                  title="Salvar"
                  aria-label="Salvar título"
                  class="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-500/10"
                >
                  <CheckCircle2 className="size-4" />
                </button>
              </div>
            ) : (
              <h1
                class="flex items-center gap-1.5 truncate text-lg font-bold cursor-pointer group"
                onClick={() => {
                  if (!lec) return;
                  setTitleDraft(lec.title);
                  setEditingTitle(true);
                }}
                title="Clique para renomear"
              >
                {lec?.title ?? "Carregando…"}
                {lec && (
                  <Pencil className="size-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
                )}
              </h1>
            )}
            {lec && (
              <div class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                <span>{KIND_LABEL[lec.kind]}</span>
                {lec.theme && <span>· {lec.theme}</span>}
                {lec.durationSeconds > 0 && (
                  <span>· {fmtDuration(lec.durationSeconds)}</span>
                )}
                <span>· {fmtDate(lec.createdAt)}</span>
                <StatusBadge status={lec.status} />
              </div>
            )}
          </div>
          <div class="flex shrink-0 items-center gap-1">
            {lec?.status === "ready" && lec.transcript && (
              <button
                type="button"
                onClick={exportSrt}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
                title="Exportar como legenda .srt (player de vídeo carrega automaticamente se o arquivo .srt tiver o mesmo nome do .webm)"
              >
                <FileText className="size-3.5" />
                Exportar .srt
              </button>
            )}
            {lec &&
              lec.status === "ready" &&
              !lec.sourceLectureId &&
              flagEnabled("student.menu.shares", false) && (
                <button
                  type="button"
                  onClick={() =>
                    openShareDialog({
                      kind: "lecture",
                      resourceId: lec.id,
                      resourceTitle: lec.title
                    })
                  }
                  class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
                  title="Compartilhar com outros alunos"
                >
                  <Share2 className="size-3.5" />
                  Compartilhar
                </button>
              )}
            <button
              type="button"
              onClick={remove}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="size-3.5" />
              Excluir
            </button>
          </div>
        </div>

        {lec && (
          <div class="ml-[5.75rem] rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            <span class="font-medium">Arquivo da gravação:</span>{" "}
            <code class="rounded bg-background px-1.5 py-0.5 font-mono text-foreground/80">
              {lectureBasename(lec)}.webm
            </code>
            {lec.transcript && (
              <span>
                {" "}
                — legenda equivalente:{" "}
                <code class="rounded bg-background px-1.5 py-0.5 font-mono text-foreground/80">
                  {lectureBasename(lec)}.srt
                </code>
              </span>
            )}
          </div>
        )}

        {lec?.linkedAiStudySessionId && (
          <a
            href={`/student/?view=ai-study&id=${encodeURIComponent(lec.linkedAiStudySessionId)}`}
            class="ml-[5.75rem] inline-flex items-center gap-1.5 self-start rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
            title="Abrir o caderno vinculado"
          >
            <Sparkles className="size-3" />
            Vinculada a um caderno do Modo Estudo
          </a>
        )}

        {lec?.sourceLectureId && (
          <div class="ml-[5.75rem] inline-flex items-center gap-1.5 self-start rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-[11px] font-medium text-blue-700 dark:text-blue-300">
            <Share2 className="size-3" />
            Compartilhada
            {lec.sharedByName ? (
              <> por <strong>{lec.sharedByName}</strong></>
            ) : (
              <> com você</>
            )}
            {" "}· seu progresso é independente
          </div>
        )}
      </header>

      {loading && !lec && (
        <div class="space-y-2">
          <Skeleton class="h-32 w-full" />
        </div>
      )}

      {err && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
          <AlertCircle className="mr-1 inline size-4 text-destructive" />
          {err}
        </div>
      )}

      {lec && lec.status === "processing" && (
        <div class="rounded-md border border-blue-500/40 bg-blue-500/5 px-3 py-3 text-sm">
          <Loader2 className="mr-1 inline size-4 animate-spin text-blue-600" />
          Processando transcrição e resumo… isso costuma levar entre 30s e
          alguns minutos dependendo da duração.
        </div>
      )}

      {lec && lec.status === "failed" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3 text-sm">
          <AlertCircle className="mr-1 inline size-4 text-destructive" />
          <strong>Falhou ao processar.</strong>{" "}
          {lec.processError ?? "Erro não detalhado."}
        </div>
      )}

      {lec && lec.status === "ready" && (
        <>
          {/* Side-by-side em xl+, stack em telas menores */}
          <div class="grid gap-4 xl:grid-cols-2">
            <SummaryPanel
              text={lec.summary ?? ""}
              onCopy={() => copyText(lec.summary ?? "")}
              onDownload={() =>
                downloadText(`${lec.title}-resumo.md`, lec.summary ?? "")
              }
            />
            <TranscriptPanel
              text={lec.transcript ?? ""}
              onCopy={() => copyText(lec.transcript ?? "")}
              onDownload={() =>
                downloadText(`${lec.title}-transcricao.md`, lec.transcript ?? "")
              }
              onRenameSpeaker={renameSpeaker}
            />
          </div>

          <footer class="flex items-center justify-end gap-2 text-[11px] text-muted-foreground">
            <CheckCircle2 className="size-3 text-emerald-600" />
            {lec.aiProvider === "gemini-byok"
              ? "Gerado com sua chave Gemini (BYOK)"
              : `Gerado pela plataforma (${lec.aiModel ?? "—"})`}
          </footer>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Painéis
// =============================================================================

function PanelHeader({
  icon: Icon,
  label,
  accent,
  onCopy,
  onDownload
}: {
  icon: typeof Sparkles;
  label: string;
  accent: string;
  onCopy: () => void;
  onDownload: () => void;
}) {
  return (
    <div class="flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-2">
      <div class={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${accent}`}>
        <Icon className="size-3.5" />
        {label}
      </div>
      <div class="flex items-center gap-1">
        <button
          type="button"
          onClick={onCopy}
          title="Copiar"
          aria-label="Copiar"
          class="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Copy className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={onDownload}
          title="Download .md"
          aria-label="Download .md"
          class="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Download className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

function SummaryPanel({
  text,
  onCopy,
  onDownload
}: {
  text: string;
  onCopy: () => void;
  onDownload: () => void;
}) {
  return (
    <section class="overflow-hidden rounded-md border border-border bg-card">
      <PanelHeader
        icon={Sparkles}
        label="Resumo"
        accent="text-violet-700 dark:text-violet-300"
        onCopy={onCopy}
        onDownload={onDownload}
      />
      <div class="max-h-[70vh] overflow-y-auto px-4 py-4">
        {text ? (
          <AiMarkdownBlock text={text} />
        ) : (
          <p class="text-sm text-muted-foreground italic">(sem resumo)</p>
        )}
      </div>
    </section>
  );
}

function TranscriptPanel({
  text,
  onCopy,
  onDownload,
  onRenameSpeaker
}: {
  text: string;
  onCopy: () => void;
  onDownload: () => void;
  onRenameSpeaker: (oldTag: string, newName: string) => void | Promise<void>;
}) {
  return (
    <section class="overflow-hidden rounded-md border border-border bg-card">
      <PanelHeader
        icon={FileText}
        label="Transcrição"
        accent="text-emerald-700 dark:text-emerald-300"
        onCopy={onCopy}
        onDownload={onDownload}
      />
      <div class="max-h-[70vh] overflow-y-auto px-4 py-4">
        {text ? (
          <TranscriptRenderer text={text} onRenameSpeaker={onRenameSpeaker} />
        ) : (
          <p class="text-sm text-muted-foreground italic">(sem transcrição)</p>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// TranscriptRenderer — parsing + badges + rename inline
// =============================================================================

/** Um turno de fala: tempo de início + falante + texto. Quando o transcript
 *  não tem timestamp ou falante explícito, esses campos ficam null/"" e o
 *  bloco renderiza minimalista. */
interface Turn {
  time: string | null;
  speaker: string | null;
  text: string;
}

// Regex que captura [HH:MM:SS], [MM:SS] (apenas dígitos+:) ou [Qualquer Coisa]
const TAG_RE = /\[([^\]]+)\]/g;

function isTimecode(s: string): boolean {
  return /^\d{1,2}(?::\d{2}){1,2}$/.test(s.trim());
}

/** Quebra o transcript em turnos. Suporta 3 formatos:
 *   1. [MM:SS] [Voz X] texto  → novo formato (ideal)
 *   2. [Voz X] texto [MM:SS]  → legacy (tempo no fim)
 *   3. texto puro             → bloco único sem header
 *
 * Estratégia: walk linear, sempre que ver speaker tag começa novo turno;
 * o tempo associado é o último [MM:SS] visto até aquele ponto.
 */
function parseTranscript(raw: string): Turn[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const turns: Turn[] = [];
  let pendingTime: string | null = null;
  let current: Turn | null = null;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  TAG_RE.lastIndex = 0;
  while ((m = TAG_RE.exec(trimmed)) !== null) {
    const beforeText = trimmed.slice(lastIdx, m.index).trim();
    // O texto entre tags é apêndice do turno atual (se houver)
    if (beforeText && current) {
      current.text = (current.text ? current.text + " " : "") + beforeText;
    } else if (beforeText && !current) {
      // Texto solto antes de qualquer tag → bloco "sem cabeçalho"
      current = { time: null, speaker: null, text: beforeText };
    }
    const inner = m[1].trim();
    if (isTimecode(inner)) {
      // Se o timecode vem ANTES do speaker (novo formato), guarda como pendingTime
      // Se vem DEPOIS do texto sem speaker subsequente (legacy), atribui ao
      // turno atual (se ainda não tem time).
      if (current && !current.time) {
        current.time = inner;
      } else {
        pendingTime = inner;
      }
    } else {
      // Speaker — novo turno. Fecha o atual se existir.
      if (current) {
        turns.push(current);
      }
      current = {
        time: pendingTime,
        speaker: inner,
        text: ""
      };
      pendingTime = null;
    }
    lastIdx = m.index + m[0].length;
  }
  // Resto do texto
  const rest = trimmed.slice(lastIdx).trim();
  if (rest && current) {
    current.text = (current.text ? current.text + " " : "") + rest;
  } else if (rest && !current) {
    current = { time: pendingTime, speaker: null, text: rest };
  }
  if (current) turns.push(current);
  // Se um pendingTime sobrou sem speaker associado, fica órfão — ignora.
  return turns;
}

// Paleta determinística por nome — mesmo speaker tem mesma cor sempre.
// `borderLeft` é uma cor saturada pra marcar o bloco da fala lateralmente.
const SPEAKER_COLORS = [
  { bg: "bg-violet-500/15", text: "text-violet-700 dark:text-violet-300", ring: "ring-violet-500/30", borderLeft: "border-l-violet-500" },
  { bg: "bg-sky-500/15", text: "text-sky-700 dark:text-sky-300", ring: "ring-sky-500/30", borderLeft: "border-l-sky-500" },
  { bg: "bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-500/30", borderLeft: "border-l-emerald-500" },
  { bg: "bg-amber-500/15", text: "text-amber-700 dark:text-amber-300", ring: "ring-amber-500/30", borderLeft: "border-l-amber-500" },
  { bg: "bg-rose-500/15", text: "text-rose-700 dark:text-rose-300", ring: "ring-rose-500/30", borderLeft: "border-l-rose-500" },
  { bg: "bg-teal-500/15", text: "text-teal-700 dark:text-teal-300", ring: "ring-teal-500/30", borderLeft: "border-l-teal-500" },
  { bg: "bg-blue-500/15", text: "text-blue-700 dark:text-blue-300", ring: "ring-blue-500/30", borderLeft: "border-l-blue-500" },
  { bg: "bg-indigo-500/15", text: "text-indigo-700 dark:text-indigo-300", ring: "ring-indigo-500/30", borderLeft: "border-l-indigo-500" }
];

function speakerColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return SPEAKER_COLORS[hash % SPEAKER_COLORS.length];
}

function TranscriptRenderer({
  text,
  onRenameSpeaker
}: {
  text: string;
  onRenameSpeaker: (oldTag: string, newName: string) => void | Promise<void>;
}) {
  const turns = useMemo(() => parseTranscript(text), [text]);
  if (turns.length === 0) {
    return (
      <p class="text-sm text-muted-foreground italic">(sem transcrição)</p>
    );
  }
  return (
    <div class="space-y-3">
      {turns.map((turn, i) => (
        <TurnBlock
          key={`${i}-${turn.speaker ?? "x"}-${turn.time ?? ""}`}
          turn={turn}
          onRenameSpeaker={onRenameSpeaker}
        />
      ))}
    </div>
  );
}

function TurnBlock({
  turn,
  onRenameSpeaker
}: {
  turn: Turn;
  onRenameSpeaker: (oldTag: string, newName: string) => void | Promise<void>;
}) {
  // Cor da borda esquerda = cor do falante (visual cue rápido na lista)
  const color = turn.speaker ? speakerColor(turn.speaker) : null;
  // Se não tem speaker nem time, é um bloco "raw" — renderiza só o texto
  if (!turn.speaker && !turn.time) {
    return (
      <div class="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
        {turn.text}
      </div>
    );
  }
  return (
    <div
      class={`rounded-md border border-border bg-background/40 px-3 py-2 ${
        color ? `border-l-2 ${color.borderLeft}` : ""
      }`}
    >
      <header class="mb-1.5 flex items-center gap-2 text-[11px]">
        {turn.time && (
          <span
            class="inline-flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold text-muted-foreground"
            title="Início do turno"
          >
            <Clock className="size-2.5" />
            {turn.time}
          </span>
        )}
        {turn.speaker && (
          <SpeakerBadge
            name={turn.speaker}
            onRename={(newName) => onRenameSpeaker(turn.speaker!, newName)}
          />
        )}
      </header>
      <p class="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
        {turn.text || (
          <span class="italic text-muted-foreground">(sem fala)</span>
        )}
      </p>
    </div>
  );
}

function SpeakerBadge({
  name,
  onRename
}: {
  name: string;
  onRename: (newName: string) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const color = speakerColor(name);

  if (editing) {
    return (
      <span
        class={`mx-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 ring-1 ${color.bg} ${color.ring}`}
      >
        <input
          type="text"
          value={value}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setEditing(false);
              if (value.trim() !== name) void onRename(value);
            } else if (e.key === "Escape") {
              setEditing(false);
              setValue(name);
            }
          }}
          autoFocus
          aria-label="Renomear falante"
          title="Renomear falante"
          class={`w-32 rounded bg-transparent px-1 py-0 text-[11px] font-bold outline-none ${color.text}`}
        />
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            if (value.trim() !== name) void onRename(value);
          }}
          title="Aplicar (Enter)"
          aria-label="Aplicar"
          class="rounded p-0.5 text-emerald-600 hover:bg-emerald-500/20"
        >
          <CheckCircle2 className="size-3" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setValue(name);
        setEditing(true);
      }}
      title={`Clique pra renomear "${name}" em toda a gravação`}
      class={`mx-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold ring-1 transition-colors hover:ring-2 ${color.bg} ${color.text} ${color.ring}`}
    >
      <Volume2 className="size-2.5" />
      {name}
      <Pencil className="size-2.5 opacity-50" />
    </button>
  );
}

export { load as reloadLectures };
